import { useState } from 'react';

import { CalendarClock, Workflow, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  useCancelScheduledWorkflowRun,
  useScheduledWorkflowRuns,
} from '@/hooks/useScheduledWorkflowRuns';

import {
  SCHEDULED_WORKFLOW_RUN_STATUS_LABELS,
  SCHEDULED_WORKFLOW_RUN_STATUSES,
} from '@repo/shared-types';

import type {
  ScheduledWorkflowRunStatus,
  ScheduledWorkflowRunWithRelations,
} from '@repo/shared-types';

const STATUS_TONE: Record<ScheduledWorkflowRunStatus, string> = {
  pending: 'bg-blue-500/10 text-blue-500',
  running: 'bg-amber-500/10 text-amber-500',
  completed: 'bg-emerald-500/10 text-emerald-500',
  failed: 'bg-red-500/10 text-red-500',
  cancelled: 'bg-gray-500/10 text-gray-400',
};

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const RunRow = ({ row }: { row: ScheduledWorkflowRunWithRelations }) => {
  const { toast } = useToast();
  const cancel = useCancelScheduledWorkflowRun();
  const canCancel = row.status === 'pending';

  const handleCancel = async () => {
    try {
      const { cancelled } = await cancel.mutateAsync(row.id);
      toast({
        title: cancelled ? 'Scheduled run cancelled' : 'Already started or cancelled',
        variant: cancelled ? undefined : 'destructive',
      });
    } catch {
      toast({ title: 'Failed to cancel', variant: 'destructive' });
    }
  };

  return (
    <div className="rounded-lg border p-4 dark:border-gray-700">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="bg-muted inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium">
              <Workflow className="h-3.5 w-3.5" />
              {row.workflow?.name ?? row.workflow_id}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[row.status]}`}
            >
              {SCHEDULED_WORKFLOW_RUN_STATUS_LABELS[row.status]}
            </span>
            <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
              <CalendarClock className="h-3.5 w-3.5" />
              {formatWhen(row.run_at)}
            </span>
          </div>
          <div className="text-sm font-medium">
            {row.contact?.name ? `${row.contact.name} · ` : ''}
            <span className="font-mono text-xs">{row.contact?.phone ?? 'no contact'}</span>
          </div>
          {row.dedupe_key && (
            <p className="text-muted-foreground text-[11px]">Key: {row.dedupe_key}</p>
          )}
          {row.error && <p className="text-[11px] text-red-500">{row.error}</p>}
        </div>
        {canCancel && (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive shrink-0 gap-1"
            onClick={handleCancel}
            disabled={cancel.isPending}
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

export const ScheduledWorkflowRunsList = () => {
  const [status, setStatus] = useState<string>('pending');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useScheduledWorkflowRuns({
    page,
    limit: 25,
    ...(status !== 'all' ? { status: status as ScheduledWorkflowRunStatus } : {}),
  });
  const rows = data?.data ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <Select
        value={status}
        onValueChange={(v) => {
          setStatus(v);
          setPage(1);
        }}
      >
        <SelectTrigger className="h-8 w-36 text-sm">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {SCHEDULED_WORKFLOW_RUN_STATUSES.map((s) => (
            <SelectItem key={s} value={s}>
              {SCHEDULED_WORKFLOW_RUN_STATUS_LABELS[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">No workflow runs match.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <RunRow key={row.id} row={row} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-muted-foreground text-xs">
            Page {page} of {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};
